import { useEffect, useState } from 'react'
import { Plus, Calendar as CalendarIcon, List, Clock, Users, TrendingUp, AlertCircle } from 'lucide-react'
import { useCoachStore } from '@/stores/coachStore'
import { useSessionStore } from '@/stores/sessionStore'
import { CalendarView } from '@/components/coach/CalendarView'
import { SessionListView } from '@/components/coach/SessionListView'
import { SessionDetailModal } from '@/components/coach/SessionDetailModal'
import { SessionFormModal } from '@/components/coach/SessionFormModal'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { apiService } from '@/services/api'

export function SchedulePage() {
  const { setCurrentPage } = useCoachStore()
  const { 
    openAddSessionModal, 
    openSessionDetailModal,
    getTodaysSessions,
    getUpcomingSessions,
    sessions,
    setSessions
  } = useSessionStore()

  const [activeTab, setActiveTab] = useState<'calendar' | 'list'>('calendar')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setCurrentPage('schedule')
    loadSessions()
  }, [setCurrentPage])

  const loadSessions = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      // Fetch sessions from backend
      const response = await apiService.getSessions({ 
        page: 1, 
        limit: 100,
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // Next 30 days
      })
      
      if (response.success && response.data) {
        setSessions((response.data.data || []).map((session: any) => ({
          ...session,
          date: session.date || new Date().toISOString().split('T')[0],
          startTime: session.startTime || '09:00',
          endTime: session.endTime || '10:00'
        })))
      } else {
        throw new Error(response.error || 'Failed to load sessions')
      }
    } catch (err) {
      console.error('Failed to load sessions:', err)
      setError('Failed to load sessions. Using cached data.')
      // Keep existing mock data as fallback
    } finally {
      setIsLoading(false)
    }
  }

  const coachId = 'coach-001' // This would come from auth context
  const todaysSessions = getTodaysSessions(coachId)
  const upcomingSessions = getUpcomingSessions(coachId, 7)
  const totalSessions = sessions.length
  const completedSessions = sessions.filter(s => s.status === 'completed').length

  const handleSessionClick = (session: any) => {
    openSessionDetailModal(session)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-warning-600 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm text-warning-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="coach-header">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedule</h1>
          <p className="text-gray-600">
            Manage your coaching sessions and availability
          </p>
        </div>
        
        <button 
          onClick={openAddSessionModal}
          className="coach-button-primary px-4 py-2"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Session
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="coach-section p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Today's Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{todaysSessions.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="coach-section p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Week</p>
              <p className="text-2xl font-bold text-gray-900">{upcomingSessions.length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CalendarIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="coach-section p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{totalSessions}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="coach-section p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completion Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {totalSessions > 0 ? Math.round((completedSessions / totalSessions) * 100) : 0}%
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('calendar')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'calendar'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <CalendarIcon className="w-4 h-4" />
            Calendar View
          </button>
          <button
            onClick={() => setActiveTab('list')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'list'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <List className="w-4 h-4" />
            List View
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-[600px]">
        {activeTab === 'calendar' ? (
          <CalendarView onSessionClick={handleSessionClick} />
        ) : (
          <SessionListView onSessionClick={handleSessionClick} />
        )}
      </div>

      {/* Modals */}
      <SessionDetailModal />
      <SessionFormModal />
    </div>
  )
}